import { useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard } from "lucide-react";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import NotificationBell from "../components/notifications/NotificationBell";
import { Button } from "../components/ui/button";
import { useTheme } from "../contexts/ThemeContext";

/**
 * Stripe Connect · How it works
 * ─────────────────────────────
 * Standalone page (replaces the modal that lived inside AffiliatePayoutsPanel).
 * Uses the standard app-shell header — Back-arrow, page title, CoinBalancePill,
 * NotificationBell, AdminMenu, MainMenu — matching the /relationships and
 * /affiliate header pattern.
 */
export default function StripeConnectInfoPage() {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const isCyber = currentTheme === "cyber_agent";
  const accentText = isCyber ? "text-cyan-400" : "text-divine-gold";

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Header — mirrors AffiliatePage / MatchDetail shell */}
      <header className="border-b border-white/10 bg-divine-black-light sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/affiliate");
              }}
              className="text-white/60 hover:text-white p-2 flex-shrink-0"
              data-testid="stripe-connect-info-back-btn"
              title="Back to Affiliate"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className={`text-lg ${isCyber ? "text-white font-semibold" : "font-cinzel text-white"} truncate`}>
              Stripe Connect
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-xl border border-white/10 bg-[#111113] p-5 sm:p-6">
          <h2 className={`text-sm font-semibold ${accentText} flex items-center gap-2 mb-4`}>
            <CreditCard className="w-4 h-4" /> How it works
          </h2>
          <div className="space-y-5 text-[13px] text-white/70 leading-relaxed">
            <div>
              <p className={`font-semibold ${accentText} mb-1`}>What Connect does</p>
              <p>
                Connecting Stripe creates a new{" "}
                <em className="font-bold italic text-white/85">Stripe Express</em>{" "}
                account under Agent Eros that lets us pay your affiliate commissions
                directly to your bank. It&apos;s separate from any personal or business
                Stripe account you already run — your existing accounts are untouched.
              </p>
            </div>
            <div>
              <p className={`font-semibold ${accentText} mb-1`}>Reuse your existing Stripe details</p>
              <p>
                To avoid re-entering your bank, tax and ID info, use the{" "}
                <em className="font-bold italic text-white/85">same email and phone number</em>{" "}
                you use on your current Stripe account when you go through onboarding.
                On Stripe&apos;s page, choose{" "}
                <em className="font-bold italic text-white/85">&quot;Sign in with Stripe&quot;</em>{" "}
                and your verified identity, bank account and tax details flow across
                in seconds. Payouts land in the same bank account you already use.
              </p>
            </div>
            <div className="pt-1">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_538a19b8-89e2-4971-abe6-259a336b0e5c/artifacts/mlo2vpxh_eros-stripe-wallet-bank.png"
                alt="How Agent Eros routes affiliate payments through your Stripe Express wallet to your bank"
                className="w-full rounded-lg border border-white/10 p-2"
                style={{ backgroundColor: "#ffe6b6" }}
                data-testid="stripe-connect-info-diagram"
                loading="lazy"
              />
            </div>
            <div>
              <p className={`font-semibold ${accentText} mb-1`}>New Stripe Express user account</p>
              <p>
                Don&apos;t have a Stripe account yet? No problem — you can create one
                in a few minutes and have affiliate payouts landing in your bank
                shortly after. Stripe will ask for a quick{" "}
                <em className="font-bold italic text-white/85">ID verification</em>{" "}
                and your{" "}
                <em className="font-bold italic text-white/85">true legal name</em>;
                both are standard for anyone receiving payouts and only take a moment.
              </p>
            </div>
            <div className="pt-1">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_538a19b8-89e2-4971-abe6-259a336b0e5c/artifacts/5qr45mw3_eros-wallet-bank.png"
                alt="New Stripe Express Agent Eros wallet routing payouts from Agenteros.ai to your bank"
                className="w-full rounded-lg border border-white/10 p-2"
                style={{ backgroundColor: "#ffe6b6" }}
                data-testid="stripe-connect-info-diagram-new"
                loading="lazy"
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/10 flex justify-start">
            <Button
              variant="ghost"
              onClick={() => navigate("/affiliate")}
              className="text-white/60 hover:text-white gap-2"
              data-testid="stripe-connect-info-return-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to Affiliate
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
